"use client";

import { useId, useMemo, useState } from "react";

import { CcTrafficSliderField } from "@/components/services/seo/CcTrafficSliderField";
import {
  formatWholeNumber,
  clampPercentForSlider,
  clampWholeForSlider,
  calculatorCurrency,
  moneyRange,
} from "@/components/services/seo/seo-revenue-projection";
import { CcSliderResultsCalculatorLayout } from "@/components/services/shared/calculator/CcSliderResultsCalculatorLayout";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";

import { CcSemProfitResultsPanel } from "./CcSemProfitResultsPanel";
import type { SemSubpageCopy } from "./sem-page-copy";
import {
  projectSemProfit,
  SEM_PROFIT_SLIDER_RANGES,
  type SemProfitField,
} from "./sem-profit-projection";

type CcSemProfitCalculatorProps = {
  copy: SemSubpageCopy["profitCalculator"];
};

export function CcSemProfitCalculator({
  copy,
}: CcSemProfitCalculatorProps) {
  const ids = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const locale = copy.locale;
  const ranges = useMemo(
    () => ({
      ...SEM_PROFIT_SLIDER_RANGES,
      budget: moneyRange(SEM_PROFIT_SLIDER_RANGES.budget, locale),
      cpc: moneyRange(SEM_PROFIT_SLIDER_RANGES.cpc, locale),
      customerValue: moneyRange(SEM_PROFIT_SLIDER_RANGES.customerValue, locale),
    }),
    [locale],
  );

  const [budget, setBudget] = useState(
    formatWholeNumber(ranges.budget.fallback, locale),
  );
  const [cpc, setCpc] = useState(
    formatWholeNumber(ranges.cpc.fallback, locale),
  );
  const [conversion, setConversion] = useState(
    String(ranges.conversion.fallback),
  );
  const [close, setClose] = useState(
    String(ranges.close.fallback),
  );
  const [customerValue, setCustomerValue] = useState(
    formatWholeNumber(
      ranges.customerValue.fallback,
      locale,
    ),
  );

  const result = projectSemProfit({
    budget: clampWholeForSlider(budget, ranges.budget),
    cpc: clampWholeForSlider(cpc, ranges.cpc),
    conversionPercent: clampPercentForSlider(conversion, ranges.conversion),
    closePercent: clampPercentForSlider(close, ranges.close),
    customerValue: clampWholeForSlider(customerValue, ranges.customerValue),
  });

  const money = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency: calculatorCurrency(locale),
        maximumFractionDigits: 0,
      }),
    [locale],
  );
  const number = useMemo(
    () => new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }),
    [locale],
  );
  const ratio = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }),
    [locale],
  );

  const slider = (
    field: SemProfitField,
    label: string,
    raw: string,
    onChange: (next: string) => void,
    kind: "whole" | "percent",
    unit?: string,
  ) => (
    <CcTrafficSliderField
      id={`${ids}-${field}`}
      field={field}
      label={label}
      raw={raw}
      onChange={onChange}
      kind={kind}
      range={ranges[field]}
      locale={locale}
      unit={unit}
    />
  );

  return (
    <section
      className="cc-section cc-sem-profit-calculator"
      aria-labelledby="cc-sem-profit-calculator-title"
      data-cc-nav="light"
      {...devSectionAttr("sem-profit-calculator")}
    >
      <div className="cc-container">
        <header className="cc-seo-value__header">
          <CcSanjayaEyebrow theme="on-subtle">{copy.sectionEyebrow}</CcSanjayaEyebrow>
          <h2 id="cc-sem-profit-calculator-title" className="cc-seo-heading">
            {copy.sectionTitle}
          </h2>
          <p className="cc-seo-lead cc-seo-value__lead">{copy.sectionLead}</p>
        </header>

        <div className="cc-traffic" data-traffic-calculator-version="2">
          <CcSliderResultsCalculatorLayout
            showHeader={false}
            controls={
              <>
                <p className="cc-traffic__text cc-sem-profit-calculator__intro">
                  {copy.text}
                </p>
                {slider(
                  "budget",
                  copy.budgetLabel,
                  budget,
                  setBudget,
                  "whole",
                  copy.budgetUnit,
                )}
                {slider(
                  "cpc",
                  copy.cpcLabel,
                  cpc,
                  setCpc,
                  "whole",
                  copy.cpcUnit,
                )}
                {slider(
                  "conversion",
                  copy.conversionLabel,
                  conversion,
                  setConversion,
                  "percent",
                  "%",
                )}
                {slider(
                  "close",
                  copy.closeLabel,
                  close,
                  setClose,
                  "percent",
                  "%",
                )}
                {slider(
                  "customerValue",
                  copy.customerValueLabel,
                  customerValue,
                  setCustomerValue,
                  "whole",
                  copy.customerValueUnit,
                )}
              </>
            }
            results={
              <CcSemProfitResultsPanel
                copy={copy}
                result={result}
                money={money}
                number={number}
                ratio={ratio}
              />
            }
          />
        </div>
      </div>
    </section>
  );
}
